import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {DefinitionsList as DefinitionsListShared} from '@github-ui/custom-properties-core/components/DefinitionsList'
import {PropertiesPageTabs} from '@github-ui/custom-properties-core/components/PropertiesPageTabs'
import type {OrgCustomPropertiesListPagePayload} from '@github-ui/custom-properties-types'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Pagination} from '@primer/react'
import {useEffect, useState} from 'react'

import {DefinitionsList} from '../components/DefinitionsList'
import {DefinitionsPageHeader, PropertiesHeaderPageTabs} from '../components/PropertiesHeaderPageTabs'
import {usePropertiesListResults} from '../hooks/use-properties-list'
import {useEditPropertyPathBuilder} from '../hooks/use-property-details-paths'
import {DefinitionsFilter} from './DefinitionsFilter'

const debounceAnnounce = debounce(announce, 300)

export function OrgCustomPropertiesListPage() {
  const {permissions, ownDefinitionsCount, ...payload} = useRoutePayload<OrgCustomPropertiesListPagePayload>()
  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')
  const enableSharedComponents = useFeatureFlag('custom_properties_shared_components')
  const getPropertyDetailsPath = useEditPropertyPathBuilder()

  const [filterQuery, setFilterQuery] = useState('')

  const {
    payload: {totalCount, pageCount, definitions},
    currentPage,
    fetchResults,
  } = usePropertiesListResults({initialPayload: payload})

  useEffect(() => {
    if (enableSharedComponents) return
    debounceAnnounce(`${totalCount} ${totalCount === 1 ? 'property' : 'properties'} found.`)
  }, [definitions, totalCount, enableSharedComponents])

  if (enableSharedComponents) {
    return (
      <>
        <DefinitionsPageHeader
          enableSharedComponents
          ownDefinitionsCount={ownDefinitionsCount}
          permissions={permissions}
        />
        {permissions === 'all' && (
          <PropertiesPageTabs initialActiveTab="properties" definitionsCount={totalCount} className="mb-3" />
        )}
        <DefinitionsFilter
          className="mb-3"
          variant={showFilterDialog ? 'full' : 'input'}
          filterValue={filterQuery}
          onChange={setFilterQuery}
          onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
        />

        <DefinitionsListShared
          totalCount={totalCount}
          definitions={definitions}
          renderItem={definition => (
            <DefinitionsListShared.Item
              key={definition.propertyName}
              settingsLevel="org"
              definition={definition}
              linkTo={getPropertyDetailsPath(definition.propertyName)}
            />
          )}
        />

        {pageCount > 1 && (
          <Pagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={(e, page) => {
              e.preventDefault() // Prevent click event from navigating
              fetchResults({page, filterQuery})
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <PropertiesHeaderPageTabs
        activeTab="properties"
        permissions={permissions}
        ownDefinitionsCount={ownDefinitionsCount}
        totalDefinitionsCount={totalCount}
      />
      <DefinitionsFilter
        className="mb-3"
        variant={showFilterDialog ? 'full' : 'input'}
        filterValue={filterQuery}
        onChange={setFilterQuery}
        onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
      />
      <DefinitionsList totalCount={totalCount} definitions={definitions} />

      {pageCount > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={(e, page) => {
            e.preventDefault() // Prevent click event from navigating
            fetchResults({page, filterQuery})
          }}
        />
      )}
    </>
  )
}

try{ OrgCustomPropertiesListPage.displayName ||= 'OrgCustomPropertiesListPage' } catch {}